import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
    autoSearchSelector,
    GroupeTravailUtilisateurApiObject,
    useUtilisateurService,
    useUtilisateursServiceSelector,
    UtilisateurApiObject,
} from '@europrocurement/l2d-domain';

import { GroupesDeTravailJsonld as GroupeTravailDTO } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';

import { SearchBar, CenterCircularProgress, useModal } from '@europrocurement/flexy-components';
import { useDebouncedCallback } from 'use-debounce';
import { Button, Divider, Typography } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { enqueueSnackbar } from 'notistack';
import { AppDispatch } from '../../../../../redux';
import { SearchMembersWidget } from './ModalWidget/SearchMembersWidget';

/**
 * Types Declaration part.
 */
type WorkGroupUpdateReferentProps = {
    workgroup: GroupeTravailUtilisateurApiObject;
};

/**
 * Workgroup Update infos form
 *
 * Lot of Async here! Be careful while modifying...
 */
export const WorkGroupUpdateReferentForm: React.FunctionComponent<WorkGroupUpdateReferentProps> =
    function ({ workgroup }) {
        // Standard hook
        const dispatch = useDispatch<AppDispatch>();
        const theme = useTheme();
        const { modalActions } = useModal();

        // Selectors
        const searchDataSource = useUtilisateursServiceSelector(autoSearchSelector);

        // Services
        const { getAutoSearch, selectGroupeTravail, groupesTravailApi } = useUtilisateurService();

        // SearchBar state that will determine multifieldsearch value
        const [searchBarValue, setSearchBarValue] = useState<string>('');
        const [searchReady, setSearchReady] = useState<boolean>(false);

        // Other States
        const [selectedReferent, setSelectedReferent] = useState<UtilisateurApiObject | null>(null);

        // Apply standard filters parameters on mount. They should not be modified.
        useEffect(() => {
            // Reset/clean search parameters.
            dispatch({
                type: `${searchDataSource.slicename}/reset${searchDataSource.name}Pagination`,
                payload: {},
            });
            dispatch({
                type: `${searchDataSource.slicename}/reset${searchDataSource.name}Filter`,
                payload: {},
            });
            dispatch({
                type: `${searchDataSource.slicename}/reset${searchDataSource.name}Search`,
                payload: {},
            });

            // Set filters to fetch active only inscriptions full list
            dispatch({
                type: `${searchDataSource.slicename}/set${searchDataSource.name}Pagination`,
                payload: { page: 0, itemsPerPage: 5 },
            });
            dispatch({
                type: `${searchDataSource.slicename}/set${searchDataSource.name}Filter`,
                payload: { key: 'isDeleted', value: false },
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        // Dispatch the search value with delay
        const delayedSearch = useDebouncedCallback((searchValue: string) => {
            // Must use search as argument here or it will NOT be recognized by store.
            dispatch({
                type: `${searchDataSource.slicename}/set${searchDataSource.name}Search`,
                payload: { search: searchValue }, // SET SEARCH STATE VALUE HERE
            });
        }, 1000);

        /**
         * Check if searchBarValue is the same that dataSource.search
         * Needed to avoid async issues before calling getAutoSearch.
         */
        useEffect(() => {
            if (searchDataSource.search !== '' && searchDataSource.search === searchBarValue) {
                setSearchReady(true);
            } else if (searchReady !== false) {
                setSearchReady(false);
                // reset store data to avoid showing old search items
                dispatch({
                    type: `${searchDataSource.slicename}/clear${searchDataSource.name}Data`,
                    payload: {},
                });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [searchDataSource.search, searchBarValue]);

        // Launch search if conditions are met.
        useEffect(() => {
            if (searchReady) {
                dispatch(getAutoSearch({}));
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [searchReady]);

        const checkRefCandidatesId = (candidateId: number): boolean => {
            let mustBeShown: boolean = true;

            if (
                workgroup.iduserReferent?.id === candidateId ||
                selectedReferent?.id === candidateId
            ) {
                mustBeShown = false;
            }
            return mustBeShown;
        };

        /**
         * Workgroup update function.
         *
         * Submitted on validation button click.
         */
        const updateReferent = () => {
            // Handle no selection and inscriptions fetch issues.
            if (selectedReferent === null) {
                enqueueSnackbar('Aucun référent sélectionné', { variant: 'error' });
                return;
            }

            // Format workgroup DTO.
            const updatedWorkgroup: GroupeTravailDTO = {
                idGroupeTravail: workgroup['@id'],
                lib: workgroup.lib,
                domaine: workgroup.domaine?.['@id'],
                iduserReferent: selectedReferent['@id'],
                fDelete: workgroup.fDelete,
                phone: workgroup.phone,
                email: workgroup.email,
                dCreation: workgroup.dCreation,
                dCloture: workgroup.dCloture,
                inscriptions: [],
            };

            const updateWorkGroup = () => {
                groupesTravailApi
                    .apiGroupestravailIdGroupeTravailStatusPatch({
                        idGroupeTravail: `${workgroup.id}`,
                        status: 'none',
                        groupesDeTravail: updatedWorkgroup,
                    })
                    .then(() => {
                        // Close form modal.
                        modalActions.close();

                        // Add notification to user.
                        enqueueSnackbar('Le référent du groupe de travail a bien été modifié.', {
                            variant: 'success',
                        });

                        // Update store.
                        dispatch(selectGroupeTravail({ id: workgroup.id as number }));
                    })
                    .catch((error) => {
                        console.error(error);
                        const errorMessage = error.response?.data?.exception?.message;
                        enqueueSnackbar(`Une erreur est survenue: ${errorMessage}`, {
                            variant: 'error',
                        });
                    });
            };
            updateWorkGroup();
        };

        return (
            <div style={{ maxWidth: '500px' }}>
                <Typography
                    display="flex"
                    alignItems="center"
                    width="100%"
                    color={theme.palette.secondary.main}
                    paddingBottom={1}
                    variant="h4"
                >
                    Modifier le référent
                </Typography>

                <Divider
                    sx={{
                        backgroundColor: theme.palette.secondary.main,
                        borderWidth: '1px',
                        marginBottom: '-10px',
                    }}
                />

                <SearchBar
                    placeholder="Rechercher un référent par nom, prénom ou mail"
                    onChange={(e) => {
                        setSearchBarValue(e.target.value);
                        delayedSearch(e.target.value);
                    }}
                    value={searchBarValue}
                />

                <Box
                    data-testid="candidate-list-container"
                    style={{
                        height: '320px',
                        margin: '16px 0px',
                    }}
                >
                    {searchDataSource.status === 'loading' && (
                        <CenterCircularProgress sx={{ height: '100%' }} />
                    )}

                    {searchDataSource.data &&
                        searchBarValue !== '' &&
                        searchDataSource.status === 'succeeded' &&
                        searchDataSource.data.map(
                            (user) =>
                                checkRefCandidatesId(user.id as number) && (
                                    <SearchMembersWidget
                                        key={user.id}
                                        candidate={user}
                                        setCandidate={setSelectedReferent}
                                        sx={{
                                            ':hover': {
                                                backgroundColor: theme.palette.grey.A700,
                                            },
                                        }}
                                    />
                                ),
                        )}
                </Box>

                <Typography
                    variant="l2dh7"
                    sx={{ lineHeight: '1.4' }}
                >
                    Nouveau référent :
                </Typography>

                <Typography
                    variant="l2dbodyxs"
                    color={theme.palette.text.secondary}
                >
                    Cliquer sur un utilisateur pour le sélectionner
                </Typography>

                <Box
                    data-testid="selected-candidate-container"
                    sx={{
                        marginTop: '12px',
                        width: '500px',
                        height: '51px',
                    }}
                >
                    {selectedReferent ? (
                        <SearchMembersWidget
                            key={selectedReferent.id}
                            candidate={selectedReferent}
                        />
                    ) : (
                        ''
                    )}
                </Box>

                <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="flex-end"
                >
                    <Button
                        aria-label="validate-update-referent-button"
                        type="button"
                        variant="contained"
                        style={{
                            marginTop: '16px',
                        }}
                        onClick={updateReferent}
                    >
                        Valider
                    </Button>
                </Box>
            </div>
        );
    };
